﻿import {JSX, MouseEvent, ReactNode, RefObject, SubmitEvent, useCallback, useEffect, useRef} from "react";
import {createPortal} from "react-dom";

export type DialogProps = {
	title: ReactNode;
	children?: ReactNode;
	ref?: RefObject<HTMLDialogElement | null>;
	/** The dialog's `returnValue`, or undefined when it was dismissed. */
	onClose?: (returnValue?: string) => void;
	// Not intersected: <dialog> declares its own onClose as a plain event handler, and an
	// intersection would demand a function satisfying both — leaving every caller's parameter
	// typed `string | SyntheticEvent` for a value that is only ever a string.
} & Omit<React.ComponentPropsWithoutRef<"dialog">, "onClose">;

function closeDialog(evt: MouseEvent<HTMLDialogElement>): void {
	// Only this dialog's own backdrop. Nested modal dialogs are portaled to <body> but stay
	// React children of their host, so a click on the inner backdrop bubbles through the portal
	// to the outer onClick with a <dialog> target too — a nodeName check would close both.
	if (evt.target === evt.currentTarget) {
		evt.preventDefault();
		evt.currentTarget.close("--dismiss");
	}
}

export default function Dialog(props: DialogProps): JSX.Element {
	const {title, children, ref, onClose, ...rest} = props;
	const internalRef = useRef<HTMLDialogElement>(null);
	const dialogRef = ref || internalRef;
	const onCloseHandler = useCallback((evt: React.SyntheticEvent<HTMLDialogElement>) => {
		// A nested dialog is a React child of this one (both portal to <body>), and React
		// propagates its close event up the React tree — so without this a dialog would report
		// itself closed whenever a dialog inside it closed.
		if (evt.target !== dialogRef.current) {
			return;
		}
		const returnValue = dialogRef?.current?.returnValue;
		onClose?.(returnValue === "--dismiss" ? undefined : returnValue);
	}, [onClose, dialogRef]);

	const onSubmit = useCallback((evt: SubmitEvent) => {
		// The dialog is portaled to <body>, but React events still bubble through the React tree
		// (portals are transparent to it) up to any ancestor <form> — e.g. an MVVM data form.
		// Stop the submit here so only the native method="dialog" close fires, not the outer form.
		evt.stopPropagation();
	}, []);

	useEffect(() => {
		if (dialogRef.current) {
			dialogRef.current.showModal();
		}
	}, [dialogRef]);

	// Portal to <body> so the dialog's own <form method="dialog"> is never nested inside a
	// consumer's <form> (e.g. MVVM data providers), which is invalid HTML. The dialog is rendered
	// in the browser's top layer anyway; React context still flows through the portal.
	// Children live OUTSIDE the method="dialog" form for the same reason, mirrored: content may
	// carry its own <form> (e.g. an MVVM data provider hosting a generated CRUD form).
	return createPortal(
		<dialog role="alertdialog" {...rest} onClick={closeDialog} ref={dialogRef} onClose={onCloseHandler}>
			<form method="dialog" onSubmit={onSubmit}>
				<header>
					<span>{title}</span>
					<button type="submit" className="material-symbols-rounded">close</button>
				</header>
			</form>
			<section>{children}</section>
		</dialog>,
		document.body
	);
};
